// components/scans/AddScanDialog.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button,
    Grid, FormControl, InputLabel, Select, MenuItem, Typography, Box,
    IconButton, CircularProgress, InputAdornment, Divider, Alert, Chip,
    FormHelperText, Card, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Store as StoreIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { scanAPI, branchAPI } from '../../services/api';
import { searchStockItems } from '../../services/optimizedSearchService';
import OptimizedSearchableAutocomplete from '../../components/common/OptimizedSearchableAutocomplete';

// Stable reference so default props don't create a new array on every render
// (the autocomplete has effects that depend on `initialOptions`).
const EMPTY_ARRAY = [];

const EMPTY_ITEM = { _id: '', item: '', quantity: 1, price: 0, stockItem: null };

const ScanItemRow = React.memo(({
    item, index, stockItems, onUpdate, onRemove, errors, formatCurrency, t
}) => {
    const handleStockItemChange = useCallback((event, selected) => {
        onUpdate(index, 'item', selected); // full stock object or null
    }, [index, onUpdate]);

    // Prefer the object stored on the row; fall back to the preloaded list
    const selectedItem =
        item.stockItem ||
        (item._id ? stockItems.find(s => s._id === item._id) : null) ||
        null;

    return (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1 }}>
                <OptimizedSearchableAutocomplete
                    label={t('scans.selectItem')}
                    searchFunction={searchStockItems}
                    value={selectedItem}
                    onChange={handleStockItemChange}
                    getOptionLabel={(option) =>
                        option ? `${option.name} - ${formatCurrency(option.price)}` : ''
                    }
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    error={Boolean(errors?.item)}
                    helperText={errors?.item}
                    noOptionsText={t('scans.noStockItemsAvailable')}
                    initialOptions={stockItems}
                    maxOptions={20}
                    minSearchLength={1}
                    searchDelay={300}
                    placeholder={t('scans.searchItems')}
                />
            </Box>
            <TextField
                label={t('scans.quantity')}
                type="number"
                value={item.quantity}
                onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 1)}
                error={Boolean(errors?.quantity)}
                helperText={errors?.quantity}
                sx={{ width: 120 }}
                inputProps={{ min: 1 }}
            />
            <TextField
                label={t('scans.itemCost')}
                value={formatCurrency((item.price || 0) * item.quantity)}
                InputProps={{ readOnly: true }}
                sx={{ width: 150 }}
            />
            <IconButton onClick={() => onRemove(index)} color="error" size="small">
                <DeleteIcon />
            </IconButton>
        </Box>
    );
});

const BranchPricingRow = ({ pricing, branch, index, onUpdate, errors, formatCurrency, t, actualCost }) => (
    <Card sx={{ mb: 2, p: 2, backgroundColor: pricing.isAvailable ? 'background.paper' : 'action.disabledBackground' }}>
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" fontWeight="medium">{branch.name}</Typography>
                <Typography variant="caption" color="text.secondary">{branch.address}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
                <TextField
                    fullWidth size="small" type="number" label={t('scans.price')} value={pricing.price || ''}
                    onChange={(e) => onUpdate(index, 'price', e.target.value)}
                    error={Boolean(errors?.price)} helperText={errors?.price}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        inputProps: { min: 0, step: 0.01 }
                    }}
                />
            </Grid>
            <Grid item xs={12} md={3}>
                {pricing.price && actualCost > 0 && (
                    <Chip
                        size="small"
                        label={`${t('scans.margin')}: ${formatCurrency(parseFloat(pricing.price) - parseFloat(actualCost))}`}
                        color={parseFloat(pricing.price) > parseFloat(actualCost) ? "success" : "error"}
                        variant="outlined"
                    />
                )}
            </Grid>
            <Grid item xs={12} md={2}>
                <FormControlLabel
                    control={<Switch checked={pricing.isAvailable} onChange={(e) => onUpdate(index, 'isAvailable', e.target.checked)} color="primary" />}
                    label={t('scans.available')}
                />
            </Grid>
        </Grid>
    </Card>
);

const AddScanDialog = ({
    open, onClose, onScanSaved, selectedScan, stockItems = EMPTY_ARRAY,
    categories = EMPTY_ARRAY, stockLoading, isRadiologist
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [fullScanData, setFullScanData] = useState(null);
    const [scanDetailsLoading, setScanDetailsLoading] = useState(false);

    const fetchBranches = useCallback(async () => {
        if (isRadiologist) return;
        setBranchesLoading(true);
        try {
            const response = await branchAPI.getAll({ isActive: true });
            setBranches(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching branches:', error);
            toast.error(t('scans.errorFetchingBranches'));
        } finally {
            setBranchesLoading(false);
        }
    }, [isRadiologist, t]);

    const fetchScanDetails = useCallback(async (scanId) => {
        setScanDetailsLoading(true);
        setFullScanData(null);
        try {
            const response = await scanAPI.getById(scanId);
            setFullScanData(response.data.data);
        } catch (err) {
            toast.error(err.response?.data?.message || t('scans.errorFetchingDetails'));
            onClose();
        } finally {
            setScanDetailsLoading(false);
        }
    }, [onClose, t]);

    useEffect(() => {
        if (open) {
            fetchBranches();
            if (selectedScan) {
                fetchScanDetails(selectedScan._id);
            } else {
                setFullScanData(null);
            }
        }
    }, [open, selectedScan, fetchBranches, fetchScanDetails]);

    const formik = useFormik({
        initialValues: {
            name: '', category: '', description: '', actualCost: 0,
            items: [{ ...EMPTY_ITEM }],
            branchPricing: []
        },
        validate: (values) => {
            const errors = {};
            if (!values.name?.trim()) errors.name = t('scans.nameRequired');
            if (!values.category) errors.category = t('scans.categoryRequired');

            if (!values.items || values.items.length === 0 || values.items.every(item => !item._id)) {
                errors.items = t('scans.itemsRequired');
            } else {
                const itemErrors = values.items.map(item => ({
                    ...(!item._id && { item: t('scans.itemNameRequired') }),
                    ...((!item.quantity || item.quantity < 1) && { quantity: t('scans.quantityMinRequired') }),
                }));
                if (itemErrors.some(e => Object.keys(e).length > 0)) errors.items = itemErrors;
            }
            if (!isRadiologist) {
                const pricingErrors = values.branchPricing.map(p => ({
                    ...((p.isAvailable && (!p.price || parseFloat(p.price) <= 0)) && { price: t('scans.priceRequired') }),
                }));
                if (pricingErrors.some(e => Object.keys(e).length > 0)) errors.branchPricing = pricingErrors;
            }
            return errors;
        },
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const submitData = {
                    name: values.name.trim(),
                    category: values.category,
                    description: values.description?.trim() || undefined,
                    actualCost: parseFloat(values.actualCost) || 0,
                    // Maps the form's item rows (item._id is the stock item ID)
                    // into the format the backend expects: { item: "stockItemId", quantity: N }
                    items: values.items.filter(item => item._id).map(item => {
                        return { item: item._id, quantity: item.quantity || 1 };
                    }),
                    branchPricing: isRadiologist ? undefined : values.branchPricing.map(p => ({
                        branch: p.branch, price: parseFloat(p.price) || 0, isAvailable: p.isAvailable
                    }))
                };
                if (selectedScan) {
                    await scanAPI.update(selectedScan._id, submitData);
                    toast.success(t('scans.updateSuccess'));
                } else {
                    await scanAPI.create(submitData);
                    toast.success(t('scans.createSuccess'));
                }
                onScanSaved();
                onClose();
            } catch (err) {
                toast.error(err.response?.data?.message || t('scans.saveError'));
            } finally {
                setLoading(false);
            }
        },
        enableReinitialize: false,
    });

    // Cost is computed from the price stored on each row, so it works
    // for items that came from search results (not in the preloaded list).
    const calculatedCost = useMemo(() => {
        return formik.values.items.reduce(
            (total, item) => total + ((item.price || 0) * (item.quantity || 0)),
            0
        );
    }, [formik.values.items]);

    useEffect(() => {
        formik.setFieldValue('actualCost', calculatedCost.toFixed(2));
    }, [calculatedCost, formik.setFieldValue]);

    // Effect to initialize/reinitialize form values when dialog opens or data changes
    useEffect(() => {
        if (!open) return;

        if (selectedScan) { // EDIT MODE
            const isEditDataReady = fullScanData && !scanDetailsLoading && !stockLoading;
            const isBranchesReadyForEdit = !isRadiologist ? (!branchesLoading && branches.length > 0) : true;

            if (!isEditDataReady || !isBranchesReadyForEdit) return;

            const initialItems = (fullScanData.items || []).map(scanItem => {
                const raw = scanItem.item;
                if (!raw) return null;

                const rawId = typeof raw === 'object' ? raw._id : raw;
                const fromList = stockItems.find(s => s._id === rawId || s.name === rawId);
                // Populated object from backend, or the preloaded list, or a minimal placeholder
                const stockItem =
                    (typeof raw === 'object' ? { ...raw, ...fromList } : fromList) ||
                    { _id: rawId, name: String(rawId), price: 0 };

                return {
                    _id: stockItem._id,
                    item: stockItem.name,
                    quantity: scanItem.quantity,
                    price: stockItem.price ?? 0,
                    stockItem,
                };
            }).filter(Boolean);

            const branchPricingData = isRadiologist ? [] : branches.map(branch => {
                const existing = fullScanData.branchPricing?.find(bp => (bp.branch?._id || bp.branch) === branch._id);
                return { branch: branch._id, price: existing?.price?.toString() || '', isAvailable: existing ? existing.isAvailable : true };
            });

            formik.setValues({
                name: fullScanData.name || '',
                category: fullScanData.category?._id || fullScanData.category || '',
                items: initialItems.length ? initialItems : [{ ...EMPTY_ITEM }],
                description: fullScanData.description || '',
                actualCost: fullScanData.actualCost || 0,
                branchPricing: branchPricingData
            });

        } else { // CREATE MODE
            const isBranchesReadyForCreate = !isRadiologist ? (!branchesLoading && branches.length > 0) : true;
            if (!isBranchesReadyForCreate) return;

            formik.resetForm();
            const initialBranchPricing = isRadiologist ? [] : branches.map(branch => ({
                branch: branch._id, price: '', isAvailable: true
            }));
            formik.setFieldValue('branchPricing', initialBranchPricing);
            formik.setFieldValue('actualCost', 0);
            formik.setFieldValue('items', [{ ...EMPTY_ITEM }]);
        }
    }, [open, selectedScan, fullScanData, stockItems, branches, branchesLoading, isRadiologist, stockLoading, scanDetailsLoading, formik.setValues, formik.resetForm, formik.setFieldValue]);

    const updateItem = useCallback((index, field, value) => {
        const newItems = [...formik.values.items];
        if (field === 'item') {
            const stockItem = value; // full object from the autocomplete (or null)
            newItems[index] = {
                ...newItems[index],
                _id: stockItem?._id || '',
                item: stockItem?.name || '',
                price: stockItem?.price || 0,
                stockItem: stockItem || null,
            };
        } else {
            newItems[index] = { ...newItems[index], [field]: value };
        }
        formik.setFieldValue('items', newItems);
    }, [formik.values.items, formik.setFieldValue]);

    const updateBranchPricing = useCallback((index, field, value) => {
        const newPricing = [...formik.values.branchPricing];
        newPricing[index] = { ...newPricing[index], [field]: value };
        formik.setFieldValue('branchPricing', newPricing);
    }, [formik.values.branchPricing, formik.setFieldValue]);

    const formatCurrency = (amount) => new Intl.NumberFormat('ar-EG', { style: 'currency', currency: 'EGP' }).format(amount || 0);

    const handleClose = useCallback(() => {
        formik.resetForm();
        setFullScanData(null);
        onClose();
    }, [formik.resetForm, onClose]);

    const isDialogContentReady = !scanDetailsLoading && !branchesLoading && !stockLoading &&
        (selectedScan ? fullScanData !== null : true) &&
        (!isRadiologist ? branches.length > 0 : true);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>{selectedScan ? t('scans.editScan') : t('scans.createNewScan')}</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    {!isDialogContentReady ? (
                        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: '400px' }}>
                            <CircularProgress /><Typography variant="h6" sx={{ ml: 2 }}>{t('common.loading')}</Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid item xs={12}><Typography variant="h6">{t('scans.basicInformation')}</Typography></Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth name="name" label={t('scans.scanName')} value={formik.values.name} onChange={formik.handleChange} error={formik.touched.name && Boolean(formik.errors.name)} helperText={formik.touched.name && formik.errors.name} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={formik.touched.category && Boolean(formik.errors.category)}>
                                    <InputLabel>{t('scans.selectCategory')}</InputLabel>
                                    <Select value={formik.values.category} onChange={formik.handleChange} name="category" label={t('scans.selectCategory')}>
                                        {categories.map((cat) => (
                                            <MenuItem key={cat._id} value={cat._id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {cat.color && <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: cat.color }} />}
                                                    {cat.name}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {formik.touched.category && formik.errors.category && <FormHelperText>{formik.errors.category}</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth name="description" label={t('scans.description')} multiline rows={3} value={formik.values.description} onChange={formik.handleChange} placeholder={t('scans.enterOptionalDescription')} />
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="h6">{t('scans.items')}</Typography>
                                    {!isRadiologist && (
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <Chip label={`${t('scans.calculatedCost')}: ${formatCurrency(calculatedCost)}`} color="info" variant="outlined" />
                                            <TextField size="small" name="actualCost" label={t('scans.actualCost')} type="number" value={formik.values.actualCost} onChange={formik.handleChange} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, inputProps: { min: 0, step: 0.01 } }} sx={{ width: 150 }} />
                                        </Box>
                                    )}
                                </Box>
                                {formik.values.items?.map((item, index) => (
                                    <ScanItemRow
                                        key={index}
                                        item={item}
                                        index={index}
                                        stockItems={stockItems}
                                        onUpdate={updateItem}
                                        onRemove={(idx) => formik.setFieldValue('items', formik.values.items.filter((_, i) => i !== idx))}
                                        errors={formik.errors.items?.[index]}
                                        formatCurrency={formatCurrency}
                                        t={t}
                                    />
                                ))}
                                {typeof formik.errors.items === 'string' && <Typography color="error" variant="caption">{formik.errors.items}</Typography>}
                                <Button
                                    variant="outlined"
                                    onClick={() => formik.setFieldValue('items', [...formik.values.items, { ...EMPTY_ITEM }])}
                                    startIcon={<AddIcon />}
                                    sx={{ mt: 2 }}
                                >
                                    {t('scans.addItem')}
                                </Button>
                            </Grid>
                            {!isRadiologist && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} /><Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}><StoreIcon color="primary" /><Typography variant="h6">{t('scans.branchPricing')}</Typography></Box>
                                    {branches.length === 0 ? <Alert severity="error">{t('scans.noBranchesAvailable')}</Alert> : (
                                        formik.values.branchPricing?.map((pricing, index) => {
                                            const branch = branches.find(b => b._id === pricing.branch);
                                            return branch ? <BranchPricingRow key={branch._id} pricing={pricing} branch={branch} index={index} onUpdate={updateBranchPricing} errors={formik.errors.branchPricing?.[index]} formatCurrency={formatCurrency} t={t} actualCost={formik.values.actualCost} /> : null;
                                        })
                                    )}
                                    {typeof formik.errors.branchPricing === 'string' && <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>{formik.errors.branchPricing}</Typography>}
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleClose} disabled={loading || !isDialogContentReady}>{t('common.cancel')}</Button>
                    <Button type="submit" variant="contained" disabled={loading || !isDialogContentReady} startIcon={loading && <CircularProgress size={20} />}>
                        {selectedScan ? t('common.update') : t('common.create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddScanDialog;